import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, Loader2, X, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DocumentProcessor, DocumentProcessingProgress } from "@/utils/documentProcessor";
import { supabase } from "@/integrations/supabase/client";
import { convertPDFToText } from "@/services/pdfConversionService";

interface DocumentUploadProps {
  onUploadComplete: (text: string, filename?: string, fileUrl?: string, originalFile?: File) => void;
  title?: string;
  description?: string;
  acceptedTypes?: string[];
}

export function DocumentUpload({ 
  onUploadComplete, 
  title = "Upload Document",
  description = "Upload a PDF, Word document, or paste text",
  acceptedTypes = ['.pdf', '.doc', '.docx', '.txt']
}: DocumentUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<DocumentProcessingProgress | null>(null);
  const { toast } = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Automatically process the file
      await handleFileUpload(file);
    }
  };

  const uploadPdfToStorage = useCallback(async (file: File) => {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    
    setProcessingProgress({
      stage: 'loading',
      progress: 30,
      message: 'Uploading PDF...'
    });

    const { error } = await supabase.storage
      .from('uploaded-documents')
      .upload(fileName, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from('uploaded-documents')
      .getPublicUrl(fileName);

    return data.publicUrl;
  }, []);

  const handleFileUpload = useCallback(async (file?: File) => {
    const fileToProcess = file || selectedFile;
    if (!fileToProcess) return;
    
    setIsProcessing(true);
    
    try {
      // For PDFs, upload to storage and use ConvertAPI to extract text
      if (fileToProcess.type === 'application/pdf') {
        const pdfUrl = await uploadPdfToStorage(fileToProcess);
        setUploadedPdfUrl(pdfUrl);

        // Use ConvertAPI to extract text from the PDF
        try {
          const result = await convertPDFToText(pdfUrl);

          if (result.success && result.text) {
            setIsSuccess(true);
            onUploadComplete(result.text, fileToProcess.name, pdfUrl, fileToProcess);
            toast({
              title: "Upload successful!",
              description: `${fileToProcess.name} uploaded successfully`,
            });
            return;
          } else {
            throw new Error(result.error || 'Failed to extract text from PDF - no content returned');
          }
        } catch (conversionError: any) {
          console.error('ConvertAPI failed:', conversionError);
          
          // More specific error handling
          let errorMessage = "Could not extract text from PDF.";
          if (conversionError.message?.includes('ConvertAPI failed')) {
            errorMessage = "PDF text extraction service is temporarily unavailable.";
          }
          
          toast({
            title: "Upload partially successful",
            description: `${errorMessage} PDF uploaded but text extraction failed. You can view the PDF but may need to manually copy text.`,
            variant: "destructive"
          });
          
          // Still call onUploadComplete with empty text so PDF can be viewed
          setIsSuccess(true);
          onUploadComplete("", fileToProcess.name, pdfUrl, fileToProcess);
          return;
        }
      }
      
      // Process Word documents and text files
      const result = await DocumentProcessor.processDocument(fileToProcess);
      
      if (result.text) {
        setIsSuccess(true);
        onUploadComplete(result.text, fileToProcess.name, undefined, fileToProcess);
        toast({
          title: "Upload successful!",
          description: `${fileToProcess.name} uploaded successfully`,
        });
      } else {
        throw new Error('No text extracted from document');
      }
      
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "Please try again with a different document.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedFile, onUploadComplete, toast, uploadPdfToStorage]);

  const removeFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById('document-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="w-full">
          <div className="space-y-4">
            <div className="space-y-3">
              <Label htmlFor="document-upload">{title}</Label>
              <div className="space-y-3">
                <input
                  id="document-upload"
                  type="file"
                  accept={acceptedTypes.join(',')}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="document-upload"
                  className={`flex items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-muted-foreground/25 hover:border-primary/50 bg-gray-100 hover:bg-gray-50`}
                >
                  {isProcessing ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      <span className="text-sm text-primary">
                        Processing {selectedFile?.name}...
                      </span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {selectedFile ? selectedFile.name : `Choose file (${acceptedTypes.join(', ')})`}
                      </span>
                    </>
                  )}
                </label>
                
                {selectedFile && isProcessing && (
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                    <Loader2 className="w-4 h-4 text-primary animate-spin" />
                    <span className="text-sm flex-1">Processing {selectedFile.name}...</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={removeFile}
                      disabled={isProcessing}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {selectedFile && isSuccess && !isProcessing && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <FileText className="w-4 h-4 text-green-600" />
                    <span className="text-sm flex-1 text-green-800 font-medium">
                      Upload successful! {selectedFile.name}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        removeFile();
                        setIsSuccess(false);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}